// assets/js/controllers/bulk_actions_controller.ts

import { Controller } from "@hotwired/stimulus"

import Swal from "sweetalert2"

import { Modal } from "bootstrap"

import TomSelect from "tom-select"

/* ==============================
 * TYPES
 * ============================== */

type ActionType = "confirm" | "form"

interface BulkActionConfig {
    type: ActionType
    url: string
    method?: string

    /* confirm */
    confirmTitle?: string
    confirmText?: string
    confirmButtonText?: string
    cancelButtonText?: string
    confirmButtonColor?: string
    confirmIcon?: "warning" | "info" | "question" | "error"
    requiredWord?: string
    inputLabel?: string
    inputPlaceholder?: string
    inputValidationMessage?: string

    /* form */
    modalId?: string

    /* feedback */
    successTitle?: string
    successText?: string
    errorTitle?: string
    errorText?: string
}

/* ==============================
 * CONTROLLER
 * ============================== */

export default class extends Controller {

    static outlets = ["datatable"]

    static targets = ["bar", "count"]

    static values = {
        actions: Object,
        rowSelector: { type: String, default: ".bulk-row-checkbox" },
        selectAllSelector: { type: String, default: "[data-bulk-select-all]" },
    }

    declare actionsValue: Record<string, BulkActionConfig>

    declare rowSelectorValue: string

    declare selectAllSelectorValue: string

    declare readonly hasDatatableOutlet: boolean

    declare readonly datatableOutlet: any

    declare readonly hasDatatableOutletElement: boolean

    declare readonly datatableOutletElement: HTMLElement

    declare readonly barTargets: HTMLElement[]

    declare readonly countTargets: HTMLElement[]

    selectedIds = new Set<string>()

    tomSelects = new Map<string, TomSelect>()

    /* ==============================
     * LIFECYCLE
     * ============================== */

    connect(): void {

        this.element.addEventListener(
            "change",
            this.onChange
        )

        this.element.addEventListener(
            "datatable:drawn",
            this.syncCheckboxes
        )

        this.updateUi()
    }

    disconnect(): void {

        this.element.removeEventListener(
            "change",
            this.onChange
        )

        this.element.removeEventListener(
            "datatable:drawn",
            this.syncCheckboxes
        )

        this.tomSelects.forEach((ts) => ts.destroy())

        this.tomSelects.clear()

        this.selectedIds.clear()
    }

    /* ==============================
     * SELECTION
     * ============================== */

    onChange = (e: Event): void => {

        const target =
            e.target as HTMLElement

        /*
         * Select all (current page)
         */
        if (
            target.matches(
                this.selectAllSelectorValue
            )
        ) {

            this.toggleAllVisible(
                (target as HTMLInputElement).checked
            )

            return
        }

        /*
         * Row checkbox
         */
        if (
            target.matches(
                this.rowSelectorValue
            )
        ) {

            const input =
                target as HTMLInputElement

            if (input.checked) {

                this.selectedIds.add(input.value)

            } else {

                this.selectedIds.delete(input.value)
            }

            this.updateSelectAllState()

            this.updateUi()
        }
    }

    toggleAllVisible(checked: boolean): void {

        this.element
            .querySelectorAll<HTMLInputElement>(
                this.rowSelectorValue
            )
            .forEach((cb) => {

                cb.checked = checked

                if (checked) {

                    this.selectedIds.add(cb.value)

                } else {

                    this.selectedIds.delete(cb.value)
                }
            })

        this.updateUi()
    }

    syncCheckboxes = (): void => {

        const checkboxes =
            Array.from(
                this.element
                    .querySelectorAll<HTMLInputElement>(
                        this.rowSelectorValue
                    )
            )

        checkboxes.forEach((cb) => {

            cb.checked =
                this.selectedIds.has(cb.value)
        })

        this.updateSelectAllState()

        this.updateUi()
    }

    updateSelectAllState(): void {

        const selectAll =
            this.element
                .querySelector<HTMLInputElement>(
                    this.selectAllSelectorValue
                )

        if (!selectAll) {
            return
        }

        const ids =
            Array.from(
                this.element
                    .querySelectorAll<HTMLInputElement>(
                        this.rowSelectorValue
                    )
            ).map((cb) => cb.value)

        selectAll.checked =
            ids.length > 0
            && ids.every(
                (id) => this.selectedIds.has(id)
            )
    }

    updateUi(): void {

        const count = this.selectedIds.size

        this.barTargets.forEach((bar) => {

            bar.classList.toggle(
                "d-none",
                count === 0
            )
        })

        this.countTargets.forEach((el) => {

            el.textContent = String(count)
        })
    }

    /* ==============================
     * RUN ACTIONS
     * ============================== */

    run(event: Event): void {

        event.preventDefault()

        const btn =
            (event.currentTarget as HTMLElement)
            ?? (event.target as HTMLElement).closest<HTMLElement>(
                "[data-bulk-action]"
            )

        const actionId =
            btn?.dataset.bulkAction

        if (!actionId) {
            return
        }

        const cfg =
            this.actionsValue[actionId]

        if (!cfg) {

            console.warn(
                `bulk-actions: unknown action "${actionId}"`
            )

            return
        }

        if (this.selectedIds.size === 0) {
            return
        }

        if (cfg.type === "confirm") {

            this.runConfirmAction(cfg)

            return
        }

        if (cfg.type === "form") {

            this.openFormModal(actionId, cfg)
        }
    }

    async runConfirmAction(
        cfg: BulkActionConfig
    ): Promise<void> {

        const ids =
            Array.from(this.selectedIds)

        const requiredWord =
            cfg.requiredWord
            ?? ''

        const result =
            await Swal.fire({

                title:
                    cfg.confirmTitle
                    ?? "¿Confirmar acción?",

                text:
                    this.interpolate(
                        cfg.confirmText,
                        { count: ids.length }
                    ),

                icon:
                    cfg.confirmIcon
                    ?? "warning",

                showCancelButton: true,

                confirmButtonText:
                    cfg.confirmButtonText
                    ?? "Sí",

                cancelButtonText:
                    cfg.cancelButtonText
                    ?? "Cancelar",

                confirmButtonColor:
                    cfg.confirmButtonColor
                    ?? "#dc3545",

                input:
                    requiredWord
                        ? "text"
                        : undefined,

                inputLabel:
                    requiredWord
                        ? (
                            cfg.inputLabel
                            ?? `Escribí "${requiredWord}" para continuar.`
                        )
                        : undefined,

                inputPlaceholder:
                    requiredWord
                        ? (
                            cfg.inputPlaceholder
                            ?? requiredWord
                        )
                        : undefined,

                inputValidator:
                    requiredWord
                        ? (value: string | null) => {
                            if ((value || "").trim().toLowerCase() !== requiredWord.toLowerCase()) {
                                return cfg.inputValidationMessage
                                    ?? `Escribí "${requiredWord}" para continuar.`
                            }

                            return null
                        }
                        : undefined,
            })

        if (!result.isConfirmed) {
            return
        }

        await this.sendRequest(
            cfg,
            {
                ids,
                confirmationWord: requiredWord ? result.value : undefined,
            }
        )
    }

    openFormModal(
        actionId: string,
        cfg: BulkActionConfig
    ): void {

        if (!cfg.modalId) {
            return
        }

        const modalEl =
            document.getElementById(
                cfg.modalId
            )

        if (!modalEl) {
            return
        }

        /*
         * Reset fields
         */
        modalEl
            .querySelectorAll<
                HTMLInputElement
                | HTMLSelectElement
                | HTMLTextAreaElement
            >("input, select, textarea")
            .forEach((el) => {

                if (el instanceof HTMLSelectElement) {

                    if (el.multiple) {

                        Array.from(el.options).forEach((option) => {
                            option.selected = false
                        })

                    } else {

                        el.selectedIndex = 0
                    }
                } else if (
                    el instanceof HTMLInputElement
                    && el.type !== "hidden"
                    && el.type !== "submit"
                    && el.type !== "button"
                ) {

                    el.value = ""

                } else if (el instanceof HTMLTextAreaElement) {

                    el.value = ""
                }
            })

        /*
         * Init / reset TomSelect for [data-tomselect]
         */
        modalEl
            .querySelectorAll<HTMLSelectElement>(
                "select[data-tomselect]"
            )
            .forEach((select) => {

                if (!select.id) {
                    return
                }

                const existing =
                    this.tomSelects.get(select.id)

                if (existing) {

                    existing.clear(true)
                    existing.clearOptions()
                    existing.sync()

                    /*
                    * Reinsertar opciones originales del select
                    */
                    Array.from(select.options).forEach((option) => {

                        existing.addOption({
                            value: option.value,
                            text: option.text,
                            color: option.dataset.color || "secondary",
                        })
                    })

                    existing.refreshOptions(false)

                    return
                }

                const ts =
                    new TomSelect(
                        select,
                        {

                            plugins:
                                select.multiple
                                    ? ["remove_button"]
                                    : [],

                            render: {

                                item: (
                                    data: any,
                                    escape: any
                                ) => {

                                    const color =
                                        data.color
                                        || "secondary"

                                    return `
                                        <div class="badge bg-${color} text-white fs-6 px-2 py-1 ms-1 d-inline-flex align-items-center" style="border-radius: 6px;">
                                            <span>${escape(data.text)}</span>
                                        </div>`
                                },

                                option: (
                                    data: any,
                                    escape: any
                                ) => {

                                    const color =
                                        data.color
                                        || "secondary"

                                    return `
                                        <div class="py-1">
                                            <span class="badge bg-${color} text-white fs-6 px-2 py-1 me-1" style="border-radius: 6px;">
                                                ${escape(data.text)}
                                            </span>
                                        </div>`
                                },
                            },
                        }
                    )

                this.tomSelects.set(
                    select.id,
                    ts
                )
            })

        /*
         * Bind current action id to the form
         */
        const form =
            modalEl.querySelector<HTMLFormElement>(
                "form"
            )

        if (form) {

            form.dataset.bulkActionId = actionId
        }

        /*
         * Show modal
         */
        Modal.getOrCreateInstance(modalEl).show()
    }

    async submitForm(
        event: Event
    ): Promise<void> {

        event.preventDefault()

        const form =
            event.currentTarget as HTMLFormElement

        const actionId =
            form.dataset.bulkActionId

        if (!actionId) {
            return
        }

        const cfg =
            this.actionsValue[actionId]

        if (!cfg) {
            return
        }

        const ids =
            Array.from(this.selectedIds)

        if (ids.length === 0) {
            return
        }

        const payload: Record<string, any> =
            { ids }

        /*
         * Native fields
         */
        new FormData(form)
            .forEach((value, key) => {

                if (payload[key] !== undefined) {

                    if (!Array.isArray(payload[key])) {

                        payload[key] = [payload[key]]
                    }

                    payload[key].push(value)

                } else {

                    payload[key] = value
                }
            })

        /*
         * TomSelect multi values (overrides FormData)
         */
        form
            .querySelectorAll<HTMLSelectElement>(
                "select[multiple]"
            )
            .forEach((select) => {

                const ts =
                    select.id
                        ? this.tomSelects.get(select.id)
                        : undefined

                payload[select.name] =
                    ts
                        ? ts.getValue()
                        : Array.from(
                            select.selectedOptions
                        ).map((o) => o.value)
            })

        const modalEl =
            form.closest<HTMLElement>(".modal")

        const ok =
            await this.sendRequest(
                cfg,
                payload
            )

        if (ok && modalEl) {

            Modal.getInstance(modalEl)?.hide()
        }
    }

    /* ==============================
     * REQUESTS
     * ============================== */

    async sendRequest(
        cfg: BulkActionConfig,
        body: Record<string, any>
    ): Promise<boolean> {

        Swal.showLoading()

        try {

            const response =
                await fetch(
                    cfg.url,
                    {

                        method:
                            cfg.method
                            ?? "POST",

                        headers: {

                            "Content-Type": "application/json",

                            "X-Requested-With": "XMLHttpRequest",
                        },

                        body: JSON.stringify(body),
                    }
                )

            if (!response.ok) {

                throw new Error(
                    `HTTP ${response.status}`
                )
            }

            this.selectedIds.clear()

            this.updateUi()

            this.requestDatatableReload()

            Swal.fire({

                icon: "success",

                title:
                    cfg.successTitle
                    ?? "Listo",

                text:
                    cfg.successText,

                timer: 1500,

                showConfirmButton: false,
            })

            return true

        } catch (e) {

            Swal.fire({

                icon: "error",

                title:
                    cfg.errorTitle
                    ?? "Error",

                text:
                    cfg.errorText
                    ?? "Ocurrió un error al ejecutar la acción.",
            })

            return false
        }
    }

    /* ==============================
     * RELOAD DATATABLE
     * ============================== */

    /**
     * Ask the linked datatable to reload. We try three strategies in order:
     *   1) Stimulus outlet (preferred — direct method call on the controller).
     *   2) The outlet's DOM element (dispatching a custom event the datatable
     *      controller listens for). Works even when the outlet selector
     *      resolved but the controller method changed shape.
     *   3) A descendant `[data-controller~="datatable"]` element found via
     *      querySelector — last-resort fallback for cases where no outlet
     *      was declared at all.
     */
    requestDatatableReload(): void {

        /*
         * 1) Outlet (controller instance)
         */
        if (this.hasDatatableOutlet) {

            try {

                this.datatableOutlet.reload()

                return

            } catch (e) {

                console.warn(
                    "bulk-actions: datatable outlet reload failed, falling back to event",
                    e
                )
            }
        }

        /*
         * 2) Outlet (DOM element) → custom event
         */
        if (this.hasDatatableOutletElement) {

            this.datatableOutletElement.dispatchEvent(
                new CustomEvent(
                    "bulk-actions:reload",
                    { bubbles: false }
                )
            )

            return
        }

        /*
         * 3) Fallback: any descendant datatable controller
         */
        const dtEl =
            this.element.querySelector<HTMLElement>(
                "[data-controller~='datatable']"
            )

        if (dtEl) {

            dtEl.dispatchEvent(
                new CustomEvent(
                    "bulk-actions:reload",
                    { bubbles: false }
                )
            )
        }
    }

    /* ==============================
     * HELPERS
     * ============================== */

    interpolate(
        template: string | undefined,
        params: Record<string, string | number>
    ): string {

        if (!template) {
            return ""
        }

        return template.replace(
            /\{(\w+)\}/g,
            (_match, key: string) =>
                String(params[key] ?? "")
        )
    }
}
